import React, { useState, useMemo, useEffect, useRef } from 'react';
import { FiLoader, FiInfo, FiChevronDown, FiChevronUp, FiCopy, FiCheck, FiEye } from 'react-icons/fi';
import { Message, MessageContent } from '../../../types/chat';
import {
  ProcessLogStep,
  ProcessLogState,
  LlmTextContent,
  ToolRequestContent,
  ToolResultSummaryContent,
  ProcessCompleteContent,
  ProcessErrorContent
} from '../../../atoms/v0/chat/types';
import { Code, Tag, Button, Spinner, useColorModeValue, Box, Text, Avatar, Flex, IconButton, useClipboard, Tooltip, Collapse } from '@chakra-ui/react';
import { useAtom } from 'jotai';
import { messageProcessLogAtom, fetchProcessLogAtom } from '../../../atoms/v0/chat/processLogAtoms';
import { selectedCharacterAtom } from '../../../atoms/v0/characterAtoms';
import { v4 as uuidv4 } from 'uuid';
import { formatDistanceStrict } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';

const InlineSpinner: React.FC<{ className?: string }> = ({ className }) => (
  <FiLoader className={`animate-spin ${className}`} aria-hidden="true" />
);

// 渲染处理日志步骤
const renderProcessLogStep = (step: any, index: number, isUserBubble: boolean) => {
  const key = `log-step-${step.id || index}`;
  const textColor = isUserBubble ? "whiteAlpha.900" : "gray.700";
  const codeBg = isUserBubble ? "blue.800" : "gray.100";
  const codeColor = isUserBubble ? "cyan.200" : "purple.600";

  const stepData = step as ProcessLogStep;

  // Helper to format timestamp
  const formatTimestamp = (isoString: string): string => {
    if (!isoString) return '';
    try {
      return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch (e) {
      console.error("Error formatting timestamp:", e);
      return "Invalid Date";
    }
  };

  switch (stepData.processType) {
    case 'llm_text':
      return <div key={key} className={`text-xs ${isUserBubble ? 'text-whiteAlpha-800' : 'text-gray-600 dark:text-gray-400'}`}>{t('v0/chat:processLog.llmPrefix')}: {(stepData.processData as LlmTextContent)?.text}</div>;
    case 'tool_request': {
      const toolData = stepData.processData as ToolRequestContent;
      const formattedTime = formatTimestamp(stepData.timestamp);
      return (
          <div key={key} className={`text-xs p-1 rounded ${isUserBubble ? 'bg-blue-800' : 'bg-gray-100 dark:bg-gray-700'}`}>
              <span className="font-semibold">{t('v0/chat:processLog.toolRequest')}:</span> {toolData?.toolName} {t('common:tools.idLabel', { id: toolData?.toolId?.substring(0,8) })} - {t('v0/chat:processLog.status')}: {toolData?.status || t('v0/chat:processLog.unknown')}
              {formattedTime && <Text as="span" fontSize="2xs" color="gray.500" ml={1}>[{formattedTime}]</Text>}
              {(toolData?._rawInputDisplay !== undefined) &&
                <pre className={`mt-1 p-1 text-[10px] rounded ${isUserBubble ? 'bg-black/20' : 'bg-gray-200 dark:bg-gray-600'} whitespace-pre-wrap break-all`}>
                  {t('v0/chat:processLog.input')}: {toolData._rawInputDisplay}
                </pre>
              }
        </div>
      );
    }
    case 'tool_result_summary': {
      const resultData = stepData.processData as ToolResultSummaryContent;
      const formattedTime = formatTimestamp(stepData.timestamp);
      const isErrorResult = resultData?.status === 'error';
      return (
          <div key={key} className={`text-xs p-1 rounded ${isErrorResult ? (isUserBubble ? 'bg-red-800' : 'bg-red-100 dark:bg-red-900') : (isUserBubble ? 'bg-green-800' : 'bg-green-100 dark:bg-green-900')}`}>
              <span className="font-semibold">{t('v0/chat:processLog.toolResult')}:</span> {resultData?.toolName} {t('common:tools.idLabel', { id: resultData?.toolId?.substring(0,8) })} - {t('v0/chat:processLog.status')}: {resultData?.status}
              {formattedTime && <Text as="span" fontSize="2xs" color="gray.500" ml={1}>[{formattedTime}]</Text>}
              <div className={`mt-1 p-1 text-[10px] rounded ${isUserBubble ? 'bg-black/20' : 'bg-gray-200 dark:bg-gray-600'}`}> {resultData?.message} </div>
               {resultData?.data && <pre className={`mt-1 p-1 text-[10px] rounded ${isUserBubble ? 'bg-black/20' : 'bg-gray-200 dark:bg-gray-600'} whitespace-pre-wrap break-all`}>{t('v0/chat:processLog.data')}: {JSON.stringify(resultData.data, null, 2)}</pre>}
        </div>
      );
    }
    case 'process_complete':
        return <div key={key} className={`text-xs italic ${isUserBubble ? 'text-whiteAlpha-700' : 'text-gray-500 dark:text-gray-400'}`}>{ (stepData.processData as ProcessCompleteContent)?.message || t('v0/chat:processLog.processComplete')}</div>;
    case 'process_error':
        return <div key={key} className={`text-xs text-red-500 ${isUserBubble ? 'dark:text-red-300' : 'dark:text-red-400'}`}>{t('v0/chat:processLog.error')}: {(stepData.processData as ProcessErrorContent)?.message || t('common:errors.unknownError')}</div>;

    default:
      return <div key={key} className="text-xs text-gray-400">{t('v0/chat:processLog.unknownStepType')}: {String(stepData.processType)}</div>;
  }
};

// Helper function outside the component for calculating and formatting duration from steps
const calculateHistoricalDuration = (steps: ProcessLogStep[]): string | null => {
  if (!steps || steps.length < 2) return null; // Need at least two steps
  const firstStep = steps[0];
  const lastStep = steps[steps.length - 1];
  if (!firstStep?.timestamp || !lastStep?.timestamp) return null;

  try {
    const startTime = new Date(firstStep.timestamp);
    const endTime = new Date(lastStep.timestamp);
    if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) return null;

    // Calculate duration in seconds
    const durationSeconds = Math.round((endTime.getTime() - startTime.getTime()) / 1000);

    if (durationSeconds >= 0) {
      // Use formatDistanceStrict for a relative string, or format manually
      // return formatDistanceStrict(endTime, startTime, { unit: 'second' }); // e.g., "26 seconds"
      return `(Est: ${durationSeconds}s)`; // Simple format
    } else {
      return null; // Negative duration indicates an issue
    }
  } catch (e) {
    console.error("Error calculating historical duration:", e);
    return null;
  }
};

// Restore correct component definition and props destructuring
interface MessageItemProps {
  message: Message;
  index: number;
  totalMessages: number;
}

const MessageItem: React.FC<MessageItemProps> = ({ message, index, totalMessages }) => {
  const { t } = useTranslation(['v0/chat', 'v0/common', 'common']);
  const { hasCopied, onCopy } = useClipboard('');
  const [selectedCharacter] = useAtom(selectedCharacterAtom);
  const [processLogState] = useAtom(messageProcessLogAtom);
  const [, fetchProcessLog] = useAtom(fetchProcessLogAtom);
  const initiatingUserMessageId = message.parentMessageId;
  const logState = initiatingUserMessageId ? processLogState[initiatingUserMessageId] : undefined;
  const shouldHaveProcessLog = message.role === 'assistant' && message.metadata?.streaming === true && !!initiatingUserMessageId;
  const isUser = message.role === 'user';
  const isLoading = message.metadata?.status === 'loading';
  const isError = message.metadata?.status === 'error';
  const [showProcessLog, setShowProcessLog] = useState(false);
  const stableKey = message.metadata?.stableKey || message.id;
  const componentInstanceId = useRef(uuidv4().substring(0, 8)).current;
  const finalDurationDisplay = message.metadata?.processingDurationDisplay;

  // --- State for real-time elapsed timer ---
  const [elapsedTimeDisplay, setElapsedTimeDisplay] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  // ----------------------------------------

  // Ref to track previous loading state for expansion
  const prevIsLoading = useRef<boolean | undefined>(undefined);
  // Ref to track if auto-collapse has happened for this message load
  const hasAutoCollapsed = useRef(false);

  // Calculate historical duration only when needed
  const historicalDurationDisplay = useMemo(() => {
    if (!isLoading && !finalDurationDisplay && logState?.steps && logState.steps.length > 0) {
       return calculateHistoricalDuration(logState.steps);
    }
    return null;
  }, [isLoading, finalDurationDisplay, logState?.steps]);

  // Log Mount/Unmount and Key Changes
  useEffect(() => {
    console.log(`[${componentInstanceId}] MessageItem MOUNTED. Key: ${stableKey}, ID: ${message.id}`);
    return () => {
      console.log(`[${componentInstanceId}] MessageItem UNMOUNTING. Key: ${stableKey}, ID: ${message.id}`);
    };
  }, [stableKey, message.id, componentInstanceId]);

  // --- Effect for Auto-Expand based on Loading Start ---
  useEffect(() => {
    if (!shouldHaveProcessLog) return;

    // Expand when loading starts
    if (isLoading && !prevIsLoading.current) {
      console.log(`[${componentInstanceId}] --> Auto-expanding log (loading started). Key: ${stableKey}, ID: ${message.id}`);
      setShowProcessLog(true);
      hasAutoCollapsed.current = false; // Reset collapse flag on new loading start
    }

    // Update previous loading state
    prevIsLoading.current = isLoading;

  }, [isLoading, shouldHaveProcessLog, setShowProcessLog, componentInstanceId, stableKey, message.id]);
  // --------------------------------------------------------

  // --- Effect for Auto-Collapse based on Final Display Type ---
  useEffect(() => {
      if (!shouldHaveProcessLog) return;

      // Collapse when display type becomes 'final' for the first time this load cycle
      if (message.metadata?.displayType === 'final' && showProcessLog && !hasAutoCollapsed.current) {
          console.log(`[${componentInstanceId}] --> Auto-collapsing log (final display started). Key: ${stableKey}, ID: ${message.id}`);
          setShowProcessLog(false);
          hasAutoCollapsed.current = true; // Mark as collapsed for this cycle
      }
  // Dependencies: run when displayType or visibility changes
  }, [message.metadata?.displayType, showProcessLog, setShowProcessLog, hasAutoCollapsed, shouldHaveProcessLog, componentInstanceId, stableKey, message.id]);
  // -----------------------------------------------------------

  // --- Effect for real-time timer --- (MOVED INSIDE COMPONENT)
  useEffect(() => {
    const startTimeString = message.metadata?.processingStartTime;

    if (isLoading && startTimeString) {
      if (intervalRef.current) clearInterval(intervalRef.current);

      try {
        const startTimeMs = new Date(startTimeString).getTime();
        if (isNaN(startTimeMs)) {
          console.warn(`[${componentInstanceId}] Invalid start time for timer: ${startTimeString}`);
          setElapsedTimeDisplay(null); // Reset display on invalid time
          return;
        }

        const updateTimer = () => {
           const nowMs = Date.now();
           const elapsedSeconds = Math.round((nowMs - startTimeMs) / 1000);
           if (elapsedSeconds >= 0) {
             setElapsedTimeDisplay(`( ${elapsedSeconds}s )`);
           }
        };

        updateTimer(); // Initial immediate update
        intervalRef.current = setInterval(updateTimer, 1000); // Update every second

      } catch (e) {
         console.error(`[${componentInstanceId}] Error setting up timer:`, e);
         setElapsedTimeDisplay(null); // Reset on error
      }

    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Reset only if we are not loading anymore
      if (!isLoading) {
        setElapsedTimeDisplay(null);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  // Dependencies: isLoading state and the specific metadata field
  }, [isLoading, message.metadata?.processingStartTime, componentInstanceId]);
  // -------------------------------------

  // Memoize content rendering to avoid re-renders on log state changes
  const renderedContent = useMemo(() => {
    if (typeof message.content === 'string') {
      if (message.role === 'assistant') {
         return (
           <div className="prose dark:prose-invert prose-sm max-w-none message-markdown">
             <ReactMarkdown remarkPlugins={[remarkGfm]}>
               {message.content}
             </ReactMarkdown>
           </div>
         );
      } else {
         return <Text whiteSpace="pre-wrap">{message.content}</Text>;
      }
    }
    if (Array.isArray(message.content)) {
      return message.content.map((contentItem: MessageContent, idx: number) => {
        const itemKey = `${message.id || stableKey}-content-${idx}`;
        if (contentItem.type === 'text' && typeof contentItem.text === 'string') {
          if (message.role === 'assistant') {
            return (
              <div key={itemKey} className="prose dark:prose-invert prose-sm max-w-none message-markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {contentItem.text}
                </ReactMarkdown>
              </div>
            );
          } else {
            return (
              <pre key={itemKey} className="text-sm whitespace-pre-wrap font-sans">
                {contentItem.text}
              </pre>
            );
          }
        }
        return null;
      });
    }
    return null;
  }, [message.content, message.id, message.role, stableKey]);

  const handleCopy = (text: string) => {
    onCopy(); // Call the original onCopy to manage hasCopied state
    navigator.clipboard.writeText(text); // Manually copy the specific text
  };

  // Function to extract text for copying
  const getTextToCopy = (): string => {
    if (typeof message.content === 'string') {
      return message.content;
    }
    if (Array.isArray(message.content)) {
      return message.content
        .filter(item => item.type === 'text' && typeof item.text === 'string')
        .map(item => (item as { type: 'text'; text: string }).text) // Type assertion after filter
        .join('\n\n'); // Join text blocks with double newline
    }
    return '';
  };

  // --- New handler for viewing process log ---
  const handleViewProcessLog = () => {
    if (initiatingUserMessageId) {
      // Only fetch if logs aren't loaded/loading and we are about to show them
      if (!showProcessLog && (!logState || !logState.steps) && !logState?.isLoading) {
        console.log(`Fetching process log for initiating user message: ${initiatingUserMessageId}`);
        fetchProcessLog(initiatingUserMessageId);
      }
      // User manually toggles the state
      setShowProcessLog(!showProcessLog);
    } else {
        console.warn("Cannot view process log: initiatingUserMessageId (parentMessageId) is missing.");
    }
  };

  // Log right before returning the JSX
  // console.log(`[${componentInstanceId}] PRE-RENDER. Key: ${stableKey}, ID: ${message.id}, ParentID: ${initiatingUserMessageId}, showLogState: ${showProcessLog}, logCount: ${logState?.steps?.length ?? 'N/A'}`);

  return (
    <Flex
      direction="column"
      alignSelf={isUser ? 'flex-end' : 'flex-start'}
      mb={4} // Margin bottom for spacing between messages
      w="full" // Take full width to allow inner alignment
    >
      <Flex
        direction={isUser ? 'row-reverse' : 'row'}
        alignItems="flex-start" // Align avatar with the top of the message bubble
        w="full"
        position="relative" // Needed for absolute positioning of actions
      >
        {/* Avatar */}
        <Avatar
          size="sm"
          name={isUser ? 'User' : selectedCharacter?.name || 'AI'}
          src={isUser ? undefined : selectedCharacter?.avatarUrl || undefined}
          ml={isUser ? 2 : 0}
          mr={isUser ? 0 : 2}
          mt={1} // Small top margin to align better with text
        />

        {/* Message Bubble */}
        <Box
          bg={isUser ? 'blue.500' : 'gray.100'}
          color={isUser ? 'white' : 'gray.800'}
          _dark={{
            bg: isUser ? 'blue.600' : 'gray.700',
            color: isUser ? 'white' : 'whiteAlpha.900',
          }}
          px={4}
          py={2}
          borderRadius="lg"
          maxW={{ base: '85%', md: '75%' }} // Max width for the bubble
          position="relative" // Ensure bubble has position context for children
          className={`message-bubble ${isUser ? 'user-bubble' : 'ai-bubble'}`}
        >

          {/* --- Process Log Section (Rendered *inside* AI bubble) --- */}
          {shouldHaveProcessLog && (() => {
              // Log when the log area is about to be rendered
              console.log(`[${componentInstanceId}] RENDERING Log Area (shouldHaveProcessLog=true). Key: ${stableKey}, ID: ${message.id}, showProcessLog State: ${showProcessLog}`);
              // Return the actual JSX for the log area
              return (
                <div className="mt-0 mb-2 border-b border-gray-300 dark:border-gray-600 pb-2">
                  <Button
                    size="xs"
                    leftIcon={showProcessLog ? <FiChevronUp /> : <FiChevronDown />}
                    onClick={handleViewProcessLog}
                    colorScheme="gray"
                    variant="ghost"
                    isLoading={logState?.isLoading && !logState?.steps} // Show loading only initially
                    isDisabled={!initiatingUserMessageId}
                    width="full"
                    justifyContent="start"
                    fontWeight="normal" // Less emphasis
                    fontSize="xs"
                    color={isUser ? "whiteAlpha.800" : "gray.600"} // Adjust color for context
                    _dark={{ color: isUser ? "whiteAlpha.800" : "gray.400" }}
                    _hover={{ bg: isUser ? 'blue.700' : 'gray.200', _dark: { bg: isUser ? 'blue.800' : 'gray.600'} }}
                    pl={1} // Reduce padding
                    h="auto" // Auto height
                    py={0.5} // Reduce vertical padding
                  >
                    {showProcessLog ? t('v0/chat:processLog.hideProcess') : t('v0/chat:processLog.viewProcess')}
                    {/* Show EITHER live timer OR final/historical duration */}
                    {(isLoading && elapsedTimeDisplay) ? (
                      <Text as="span" fontSize="xs" color="yellow.500" ml={1} fontWeight="medium">
                        {elapsedTimeDisplay}
                      </Text>
                    ) : (finalDurationDisplay || historicalDurationDisplay) ? (
                      <Text as="span" fontSize="xs" color="gray.500" ml={1}>
                        {finalDurationDisplay || historicalDurationDisplay}
                      </Text>
                    ) : null}
                  </Button>

                  <Collapse in={showProcessLog} animateOpacity>
                    <div className={`mt-1 p-1 rounded text-xs ${isUser ? 'bg-blue.700' : 'bg-gray-50'} ${isUser ? '' : 'dark:bg-gray-800'}`}>
                      {logState?.isLoading && !logState?.steps && (
                        <div className="flex items-center justify-center py-1">
                          <Spinner size="xs" mr={2} />
                          <span className={`text-xs ${isUser ? 'text-whiteAlpha.700' : 'text-gray-500'}`}>{t('v0/common:loadingText')}...</span>
                        </div>
                      )}
                      {logState?.error && (
                        <div className="text-xs text-red-400 py-1">
                          {t('v0/chat:processLog.loadingLogFailed')}: {logState.error}
                        </div>
                      )}
                      {logState?.steps && Array.isArray(logState.steps) && logState.steps.length > 0 ? (
                        <div className="space-y-1">
                          {logState.steps.map((step, stepIndex) => {
                            if (!step) return null;
                            const isLastStep = stepIndex === (logState?.steps?.length ?? 0) - 1;
                            if (isLastStep && step?.processType === 'llm_text') {
                              return <React.Fragment key={step.id || `log-empty-${stepIndex}`}></React.Fragment>;
                            }
                            return renderProcessLogStep(step, stepIndex, isUser);
                          })}
                        </div>
                      ) : logState?.steps && logState.steps.length === 0 && !logState.isLoading ? (
                        <div className={`text-xs py-1 ${isUser ? 'text-whiteAlpha.700' : 'text-gray-500'}`}>
                          {t('v0/chat:processLog.noProcessSteps')}

                        </div>
                      ) : null}
                    </div>
                  </Collapse>
                </div>
              );
          })()}
          {/* --- End Process Log Section --- */}

          {/* --- Main Message Content Rendering --- */}
          {/* Render the memoized content */}
          {renderedContent}
          {isLoading && !isError && (
             <Spinner size="xs" ml={2} /> // Show spinner if loading
          )}
          {isError && (
            <Tag colorScheme="red" size="sm" mt={1}>{t('common:errors.errorLabel')}</Tag> // Show error tag
          )}
          {/* --- End Main Message Content Rendering --- */}

          {/* Timestamp (Positioned at bottom right) */}
          <Text
            fontSize="xs"
            color={isUser ? 'whiteAlpha.700' : 'gray.500'}
            _dark={{ color: isUser ? 'whiteAlpha.700' : 'gray.400' }}
            textAlign="right"
            mt={1} // Margin top for spacing
          >
            {message.timestamp}
          </Text>

          {/* Action Buttons (Copy) - Positioned inside */}
           {/* Only show copy for non-empty, non-loading AI messages or user messages */}
          {!isLoading && !isError && (message.role === 'user' || (message.role === 'assistant' && getTextToCopy().trim())) && (
            <Tooltip label={hasCopied ? t('common:actions.copied') : t('v0/common:copy')} placement="top" closeOnClick={false}>
              <IconButton
                aria-label={t('v0/chat:copyMessage')}
                icon={hasCopied ? <FiCheck /> : <FiCopy />}
                size="xs"
                variant="ghost"
                color={isUser ? 'whiteAlpha.800' : 'gray.500'}
                 _dark={{ color: isUser ? 'whiteAlpha.800' : 'gray.400' }}
                position="absolute"
                bottom="2px" // Adjust position as needed
                right="2px" // Adjust position as needed
                onClick={() => handleCopy(getTextToCopy())}
                zIndex={1} // Ensure it's above timestamp if overlapping
              />
            </Tooltip>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};

export default MessageItem;